// src/pages/intranet/pages/AsistenciasLogia.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Calendar, Check, X, FileText, Plus, ShieldAlert, Trash2, Clock } from 'lucide-react';

export default function AsistenciasLogia() {
  const { user } = useAuth();
  
  // Extracción segura del usuario
  const currentUserId = user?.id || user?.user?.id;
  const currentLogiaId = user?.logia_id || user?.user?.logia_id;
  const currentUserRole = user?.role || user?.user?.role;
  
  const esMaestro = currentUserRole === 'MAESTRO' || currentUserRole === 'ADMIN';
  
  const [eventos, setEventos] = useState([]);
  const [misAsistencias, setMisAsistencias] = useState([]);
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null);
  const [listaAsistencia, setListaAsistencia] = useState([]);
  
  // Estados para Modales
  const [modalNuevoEvento, setModalNuevoEvento] = useState(false);
  const [modalJustificacion, setModalJustificacion] = useState({ open: false, tipo: '', evento: null, datosExtra: null });

  useEffect(() => {
    if (currentLogiaId) cargarEventos();
    if (!esMaestro && currentUserId) cargarMisAsistencias();
  }, [currentLogiaId, currentUserId, esMaestro]);

  const cargarEventos = async () => {
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/eventos/logia/${currentLogiaId}`);
      const data = await res.json();
      if (data.success) setEventos(data.data);
    } catch (error) { console.error("Error cargando eventos", error); }
  };

  const cargarMisAsistencias = async () => {
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/usuarios/${currentUserId}/asistencias`);
      const data = await res.json();
      if (data.success) setMisAsistencias(data.data);
    } catch (error) { console.error("Error cargando asistencias", error); }
  };

  const abrirTomaAsistencia = async (evento) => {
    setEventoSeleccionado(evento);
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/eventos/${evento.id}/asistencias`);
      const data = await res.json();
      if (data.success) setListaAsistencia(data.data);
    } catch (error) { console.error("Error cargando lista", error); }
  };

  // Validación de Reglas de Negocio
  const esEventoFuturo = (fecha) => new Date(fecha) > new Date();
  
  const verificarEdicionPermitida = (fecha_evento) => {
    const horasTranscurridas = (new Date() - new Date(fecha_evento)) / (1000 * 60 * 60);
    return horasTranscurridas <= 24;
  };

  const handleToggleClick = (item) => {
    const nuevoEstado = item.estado === 'asistio' ? 'falta' : 'asistio';
    
    if (verificarEdicionPermitida(eventoSeleccionado.fecha_evento)) {
      // Edición libre dentro del plazo
      setListaAsistencia(listaAsistencia.map(a => 
        a.usuario_id === item.usuario_id ? { ...a, estado: nuevoEstado } : a
      ));
    } else {
      // Abre modal de justificación si pasaron las 24 horas
      setModalJustificacion({ 
        open: true, 
        tipo: 'modificar_asistencia', 
        evento: eventoSeleccionado, 
        datosExtra: { usuario_id: item.usuario_id, nuevo_estado: nuevoEstado } 
      });
    }
  };

  const guardarAsistencias = async () => {
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/eventos/${eventoSeleccionado.id}/asistencias`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asistencias: listaAsistencia })
      });
      const data = await res.json();
      if (data.success) {
        alert('Asistencias guardadas correctamente.');
        setEventoSeleccionado(null);
      } else {
        alert(data.error || 'Error al guardar');
      }
    } catch (error) {
      alert('Error de conexión.');
    }
  };

  const handleEliminarEvento = async (evento) => {
    if (esEventoFuturo(evento.fecha_evento)) {
      if (window.confirm('¿Está seguro de eliminar este evento programado?')) {
        try {
          const res = await fetch(`https://logia-production-725b.up.railway.app/api/eventos/${evento.id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            cargarEventos();
            if (eventoSeleccionado?.id === evento.id) setEventoSeleccionado(null);
          } else alert(data.error);
        } catch (error) { alert('Error de conexión'); }
      }
    } else {
      // Evento pasado: requiere justificación para auditoría
      setModalJustificacion({ open: true, tipo: 'eliminar_evento', evento, datosExtra: null });
    }
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in relative text-white">
      <div className="flex justify-between items-center border-b border-white/10 pb-4">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl m-0 tracking-wide">Registro de Asistencias</h1>
          <p className="text-gray-400 font-light text-sm mt-2 m-0">Control de tenidas y eventos capitulares</p>
        </div>
        {esMaestro && (
          <button onClick={() => setModalNuevoEvento(true)} className="bg-[#C5A059] text-[#050505] px-6 py-3 font-bold uppercase text-xs tracking-widest flex items-center gap-2 hover:bg-white transition-colors">
            <Plus size={16} /> Crear Evento
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
        {/* COLUMNA 1: Calendario de Eventos */}
        <div className="bg-[#050505] border border-white/5 shadow-lg p-6 relative">
          <h3 className="font-serif text-xl mb-4 flex items-center gap-2 text-[#C5A059]">
            <Calendar size={18}/> Calendario de Tenidas
          </h3>
          <div className="flex flex-col gap-3 max-h-[600px] overflow-y-auto hide-scrollbar pr-2">
            {eventos.map(evento => {
              const puedeEditar = verificarEdicionPermitida(evento.fecha_evento);
              const esFuturo = esEventoFuturo(evento.fecha_evento);
              
              return (
                <div key={evento.id} className={`bg-[#111111] border p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-colors ${eventoSeleccionado?.id === evento.id ? 'border-[#C5A059]' : 'border-white/10 hover:border-[#C5A059]/50'}`}>
                  <div>
                    <h4 className="m-0 text-md text-gray-200">{evento.titulo}</h4>
                    <span className="text-xs text-gray-500 font-mono flex items-center gap-1 mt-1">
                      <Clock size={12}/> {new Date(evento.fecha_evento).toLocaleString('es-PE')}
                    </span>
                  </div>
                  
                  <div className="flex gap-2 items-center w-full md:w-auto justify-end">
                    {evento.archivo_url && (
                      <a href={evento.archivo_url} target="_blank" rel="noreferrer" className="text-[#C5A059] hover:text-white p-2 border border-white/10" title="Ver Certificado/Acta">
                        <FileText size={16} />
                      </a>
                    )}
                    
                    {esMaestro && (
                      <>
                        <button 
                          onClick={() => abrirTomaAsistencia(evento)} 
                          className={`text-[10px] px-3 py-2 uppercase tracking-widest border font-bold transition-colors ${puedeEditar && !esFuturo ? 'border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10' : esFuturo ? 'border-blue-900/50 text-blue-400' : 'border-red-900/50 text-red-500 hover:bg-red-900/10'}`}
                        >
                          {esFuturo ? 'Programado' : puedeEditar ? 'Tomar Asistencia' : 'Auditar Asistencia'}
                        </button>
                        
                        <button 
                          onClick={() => handleEliminarEvento(evento)} 
                          className="text-red-500 hover:text-white hover:bg-red-900 p-2 border border-white/10 transition-colors" title="Eliminar Evento"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
            {eventos.length === 0 && (
              <div className="text-center text-gray-500 text-xs tracking-widest uppercase p-6">No hay eventos registrados</div>
            )}
          </div>
        </div>

        {/* COLUMNA 2: Panel de Asistencias */}
        <div className="bg-[#050505] border border-white/5 shadow-lg p-6">
          {esMaestro ? (
            eventoSeleccionado ? (
              <div className="animate-fade-in flex flex-col h-full">
                <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
                  <h3 className="font-serif text-xl text-[#C5A059] m-0">Asistencia: {eventoSeleccionado.titulo}</h3>
                  {!verificarEdicionPermitida(eventoSeleccionado.fecha_evento) && (
                    <span className="flex items-center gap-1 text-[10px] text-red-500 bg-red-900/20 px-2 py-1 font-mono tracking-widest border border-red-900/50">
                      <ShieldAlert size={14}/> CERRADO (24H)
                    </span>
                  )}
                </div>

                <div className="flex-1 flex flex-col gap-2 overflow-y-auto max-h-[450px] hide-scrollbar pr-2">
                  {listaAsistencia.map(item => (
                    <div key={item.usuario_id} className="flex justify-between items-center bg-[#111111] p-3 border border-white/5 hover:border-white/20 transition-colors">
                      <div>
                        <span className="block text-sm text-gray-200">{item.nombres} {item.apellidos}</span>
                        <span className="text-[10px] text-gray-500 tracking-widest uppercase">{item.grado}</span>
                      </div>
                      <button 
                        onClick={() => handleToggleClick(item)}
                        className={`w-10 h-10 flex items-center justify-center rounded-sm transition-all border ${item.estado === 'asistio' ? 'bg-green-600/20 text-green-400 border-green-600/50' : 'bg-red-900/20 text-red-400 border-red-900/50'}`}
                        title={verificarEdicionPermitida(eventoSeleccionado.fecha_evento) ? "Cambiar estado" : "Solicitar excepción de estado"}
                      >
                        {item.estado === 'asistio' ? <Check size={18}/> : <X size={18}/>}
                      </button>
                    </div>
                  ))}
                  {listaAsistencia.length === 0 && (
                    <div className="text-center text-gray-500 text-xs tracking-widest uppercase mt-10">Sin miembros en la Logia</div>
                  )}
                </div>

                {verificarEdicionPermitida(eventoSeleccionado.fecha_evento) && !esEventoFuturo(eventoSeleccionado.fecha_evento) && (
                  <button onClick={guardarAsistencias} className="w-full mt-6 bg-[#C5A059] text-[#050505] py-3 uppercase tracking-widest text-xs font-bold hover:bg-white transition-colors">
                    Guardar Registro Oficial
                  </button>
                )}
                {esEventoFuturo(eventoSeleccionado.fecha_evento) && (
                  <div className="w-full mt-6 bg-white/5 border border-white/10 text-gray-400 text-center py-3 uppercase tracking-widest text-xs font-bold">
                    El evento aún no ocurre
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-600 text-sm uppercase tracking-widest text-center border-2 border-dashed border-white/10 p-10">
                Seleccione un evento del calendario<br/>para gestionar las asistencias.
              </div>
            )
          ) : (
            <div>
              <h3 className="font-serif text-xl mb-4 text-[#C5A059] border-b border-white/10 pb-4 m-0">Mi Historial de Asistencia</h3>
              <div className="flex flex-col gap-2 mt-4 max-h-[500px] overflow-y-auto hide-scrollbar">
                {misAsistencias.map((asist, i) => (
                  <div key={i} className="flex justify-between items-center bg-[#111111] p-4 border border-white/5">
                    <div>
                      <p className="m-0 text-sm text-gray-200">{asist.titulo}</p>
                      <span className="text-[10px] text-gray-500 font-mono flex items-center gap-1 mt-1">
                        <Clock size={12}/> {new Date(asist.fecha_evento).toLocaleDateString()}
                      </span>
                    </div>
                    <span className={`px-3 py-1 text-[10px] tracking-widest uppercase font-mono border ${asist.estado === 'asistio' ? 'bg-green-900/30 text-green-400 border-green-800/50' : 'bg-red-900/30 text-red-400 border-red-800/50'}`}>
                      {asist.estado}
                    </span>
                  </div>
                ))}
                {misAsistencias.length === 0 && (
                  <div className="text-center text-gray-500 text-xs tracking-widest uppercase p-6">Sin historial registrado</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {modalNuevoEvento && (
        <NuevoEventoModal 
          logiaId={currentLogiaId} 
          usuarioId={currentUserId}
          onClose={() => setModalNuevoEvento(false)} 
          onSave={() => { setModalNuevoEvento(false); cargarEventos(); }} 
        />
      )}

      {modalJustificacion.open && (
        <JustificacionModal 
          config={modalJustificacion} 
          usuarioId={currentUserId}
          onClose={() => setModalJustificacion({ open: false, tipo: '', evento: null, datosExtra: null })} 
        />
      )}
    </div>
  );
}

// --- SUBCOMPONENTES ---

function NuevoEventoModal({ logiaId, usuarioId, onClose, onSave }) {
  const [formData, setFormData] = useState({ titulo: '', fecha_evento: '' });
  const [archivo, setArchivo] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    data.append('logia_id', logiaId);
    data.append('usuario_id', usuarioId);
    data.append('titulo', formData.titulo);
    data.append('fecha_evento', formData.fecha_evento);
    if (archivo) data.append('archivo_prueba', archivo);

    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/eventos', {
        method: 'POST',
        body: data
      });
      const result = await res.json();
      if (result.success) onSave();
      else alert(result.error);
    } catch (error) {
      alert('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#050505]/90 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 w-full max-w-md p-8 shadow-2xl relative text-white">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <h2 className="font-serif text-2xl m-0">Programar Evento</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={20}/></button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Título de la Tenida / Evento</label>
            <input type="text" required placeholder="Ej. Tenida Ordinaria de Grado..." value={formData.titulo} onChange={(e) => setFormData({...formData, titulo: e.target.value})} className="bg-[#050505] border border-white/10 px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Fecha y Hora</label>
            <input type="datetime-local" required value={formData.fecha_evento} onChange={(e) => setFormData({...formData, fecha_evento: e.target.value})} className="bg-[#050505] border border-white/10 px-3 py-2 text-sm text-gray-300 focus:border-[#C5A059] outline-none css-color-scheme-dark" style={{ colorScheme: 'dark' }} />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Acta o Documento (Opcional)</label>
            <input type="file" accept="image/*,.pdf" onChange={(e) => setArchivo(e.target.files[0])} className="bg-[#050505] border border-white/10 p-2 text-sm text-gray-400" />
          </div>
          
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="text-gray-400 px-4 py-2 text-xs uppercase tracking-widest hover:bg-white/5 transition-colors">Cancelar</button>
            <button type="submit" disabled={loading} className="bg-[#C5A059] text-[#050505] px-6 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white transition-colors">
              {loading ? 'Creando...' : 'Guardar Evento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function JustificacionModal({ config, onClose, usuarioId }) {
  const [justificacion, setJustificacion] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('evento_id', config.evento.id);
    formData.append('usuario_solicitante', usuarioId);
    formData.append('tipo_solicitud', config.tipo);
    formData.append('justificacion', justificacion);
    if (config.datosExtra) formData.append('detalles', JSON.stringify(config.datosExtra));
    if (archivo) formData.append('evidencia', archivo);

    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/eventos/solicitud-excepcion', {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        alert('Solicitud enviada a la Gran Secretaría para su evaluación.');
        onClose();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      alert('Error de conexión.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#050505]/90 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-red-900/50 w-full max-w-lg p-8 shadow-2xl relative text-white">
        <h2 className="font-serif text-2xl mb-2 text-red-400 flex items-center gap-2">
          <ShieldAlert size={24}/> Solicitud de Excepción Administrativa
        </h2>
        <p className="text-gray-400 text-xs mb-6 font-light leading-relaxed">
          {config.tipo === 'eliminar_evento' 
            ? 'Estás intentando eliminar un evento que ya ocurrió.' 
            : 'Estás intentando modificar un registro de asistencia fuera del plazo reglamentario de 24 horas.'}
          <br/>Debes sustentar el motivo de este cambio ante la administración central para su auditoría y aprobación.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Sustento / Justificación Detallada</label>
            <textarea 
              required 
              rows="4" 
              value={justificacion} 
              onChange={(e) => setJustificacion(e.target.value)} 
              className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none"
              placeholder="Explique el motivo del cambio..."
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Evidencia Adjunta (Opcional)</label>
            <input 
              type="file" 
              accept="image/*,.pdf" 
              onChange={(e) => setArchivo(e.target.files[0])} 
              className="bg-[#050505] border border-white/10 text-gray-400 p-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="bg-transparent text-gray-400 px-4 py-2 text-xs uppercase tracking-widest hover:text-white transition-colors">Cancelar</button>
            <button type="submit" disabled={loading} className="bg-red-900/80 border border-red-500 text-white px-6 py-2 text-xs uppercase tracking-widest font-bold hover:bg-red-700 transition-colors">
              {loading ? 'Enviando...' : 'Enviar Solicitud'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}